import { breakpointsAsInt as themeBreakpoints } from '@src/theme/breakpoints'

import { useBreakpointContext } from '@src/components/sco/utilities/BreakpointProvider/index'

export type BreakpointName = 'base' | 'sm' | 'md' | 'lg' | 'xl' | '2xl'

const BREAKPOINT_ORDER: BreakpointName[] = ['base', 'sm', 'md', 'lg', 'xl', '2xl']

/**
 * Classic OS/browser scrollbars consume ~15–17px; zoomed/themed bars can be larger.
 * Hold the current breakpoint across a dip of this size instead of using CSS
 * `scrollbar-gutter: stable` — nested/html stable gutters leave empty space
 * beside always-on classic bars.
 */
export const SCROLLBAR_GUTTER_PX = 32

// Local shim for removed @chakra-ui/utils arrayToObjectNotation (v2 internal)
function arrayToObjectNotation<T>(values: T[] | Record<string, T>, keys: string[]): Record<string, T> {
  if (!Array.isArray(values)) {
    return values as Record<string, T>
  }
  const result: Record<string, T> = {}
  keys.forEach((key, i) => {
    if (values[i] !== undefined) {
      result[key] = values[i]
    }
  })
  if (values[0] !== undefined) {
    result['base'] = values[0]
  }
  return result
}

/**
 *
 * @param breakpoint
 * @param values
 * @param arg
 */
export function getBreakpointValue<T = any>(
  breakpoint: string,
  values: Partial<Record<string, T>> | Array<T | null>
): T | undefined {
  const possibleValues = Array.isArray(values)
    ? Object.fromEntries<any>(
        Object.entries(arrayToObjectNotation(values, Object.keys(themeBreakpoints))).map(([key, value]) => [
          key,
          value,
        ])
      )
    : values

  return getClosestValue(possibleValues, breakpoint, Object.keys(themeBreakpoints))
}

/**
 *
 * @param values
 * @param arg
 */
export function containerBreakpointValue<T = any>(
  values: Partial<Record<string, T>> | Array<T | null>
): T | undefined {
  const { activeContainerBreakpoint } = useBreakpointContext()
  return getBreakpointValue(activeContainerBreakpoint, values)
}

export function rawBreakpointForWidth(widthInPx: number): BreakpointName {
  let breakpoint: BreakpointName = 'base'
  if (widthInPx >= themeBreakpoints.sm) {
    breakpoint = 'sm'
  }
  if (widthInPx >= themeBreakpoints.md) {
    breakpoint = 'md'
  }
  if (widthInPx >= themeBreakpoints.lg) {
    breakpoint = 'lg'
  }
  if (widthInPx >= themeBreakpoints.xl) {
    breakpoint = 'xl'
  }
  if (widthInPx >= themeBreakpoints['2xl']) {
    breakpoint = '2xl'
  }
  return breakpoint
}

/**
 * Maps a measured width to a breakpoint. When `previousBreakpoint` is set, a
 * shrink of up to `SCROLLBAR_GUTTER_PX` will not drop below that breakpoint —
 * classic scrollbar show/hide must not oscillate responsive layout.
 */
export function calculateBreakpointForWidth(
  widthInPx: number,
  previousBreakpoint?: BreakpointName | null
): BreakpointName {
  const next = rawBreakpointForWidth(widthInPx)
  if (!previousBreakpoint || previousBreakpoint === next) {
    return next
  }

  const previousIndex = BREAKPOINT_ORDER.indexOf(previousBreakpoint)
  const nextIndex = BREAKPOINT_ORDER.indexOf(next)
  if (nextIndex < previousIndex) {
    const currentFloor = themeBreakpoints[previousBreakpoint]
    if (widthInPx >= currentFloor - SCROLLBAR_GUTTER_PX) {
      return previousBreakpoint
    }
  }

  return next
}

/**
 *
 * @param values
 * @param breakpoint
 * @param breakpoints
 */
export function getClosestValue<T = any>(
  values: Record<string, T>,
  breakpoint: string,
  breakpoints: string[]
) {
  let index = Object.keys(values).indexOf(breakpoint)

  if (index !== -1) {
    return values[breakpoint]
  }

  let stopIndex = breakpoints.indexOf(breakpoint)

  while (stopIndex >= 0) {
    const key = breakpoints[stopIndex]

    if (values.hasOwnProperty(key)) {
      index = stopIndex
      break
    }
    stopIndex -= 1
  }

  if (index !== -1) {
    const key = breakpoints[index]
    return values[key]
  }

  return undefined
}
